import React, { useMemo, useCallback } from 'react';
import { ConfigSurvey } from '../styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Row, Col, Button, FormGroup, Label, Input } from 'reactstrap';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { MdDelete } from 'react-icons/md';
import FormTooltip from 'components/Inputs/FormTooltip';

const typesOfResponseOptions = [
  { label: 'CSAT', value: 'CSAT' },
  { label: 'NPS', value: 'NPS' },
  { label: 'Tags', value: 'TAG' },
  { label: 'Texto Livre', value: 'FREE_TEXT' }
];

const generateDefaultOptions = (typeValue) => {
  const csatLabels = [
    'Atendimento Péssimo.😡',
    'Atendimento Ruim.',
    'Atendimento Mediano.',
    'Atendimento Bom.',
    'Atendimento Excelente. 😃'
  ];

  const npsLabels = [
    'Péssimo',
    'Muito Ruim',
    'Ruim',
    'Insuficiente',
    'Mediano',
    'Suficiente',
    'Satisfatório',
    'Bom',
    'Muito bom',
    'Ótimo'
  ];

  if (typeValue === 'NPS') {
    return Array.from({ length: 10 }, (_, i) => ({
      rate: i + 1,
      label: npsLabels[i]
    }));
  }

  if (typeValue === 'CSAT') {
    return Array.from({ length: 5 }, (_, i) => ({
      rate: i + 1,
      label: csatLabels[i] || ''
    }));
  }

  if (typeValue === 'TAG') {
    return [{ tag: null, label: '', rate: '' }];
  }

  return [];
};

function QuestionOptions({ options = [], questionType, onChange, tagOptions }) {
  const typeValue = questionType?.value || questionType;
  const isTagType = typeValue === 'TAG';

  if (typeValue === 'FREE_TEXT' || !typeValue) {
    return null;
  }

  const getRateConstraints = (type) => {
    switch (type) {
      case 'TAG':
        return { min: 0, max: 9 };
      case 'NPS':
        return { min: 0, max: 10 };
      case 'CSAT':
        return { min: 1, max: 5 };
      default:
        return { min: 0, max: 10 };
    }
  };

  const { min, max } = getRateConstraints(typeValue);

  const handleAddTagOption = () => {
    const newOption = {
      tag: null,
      label: '',
      rate: ''
    };
    onChange([...options, newOption]);
  };

  const handleRemoveOption = (indexToRemove) => {
    const newOptions = options.filter((_, index) => index !== indexToRemove);
    onChange(newOptions);
  };

  const handleOptionChange = (index, field, value) => {
    if (field === 'rate' && value !== '') {
      const numValue = Number(value);
      if (numValue > max) return;
      if (numValue < min) return;
    }

    const newOptions = [...options];
    newOptions[index] = {
      ...newOptions[index],
      [field]: value
    };

    onChange(newOptions);
  };

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0" hidden={!options.length}>
          Opções de Resposta
        </h5>
        {isTagType && (
          <Button
            color="primary"
            size="sm"
            type="button"
            onClick={handleAddTagOption}
          >
            Adicionar Opção
          </Button>
        )}
      </div>
      <Row>
        {options.map((opt, optionIndex) => {
          return (
            <Col md="6" key={optionIndex} className="mb-3">
              <div
                className="p-3 rounded border"
                style={{
                  borderColor: '#eee',
                  backgroundColor: '#fafafa',
                  height: '100%'
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Resposta {optionIndex + 1}</h5>
                  {isTagType && (
                    <Button
                      color="danger"
                      size="sm"
                      type="button"
                      onClick={() => handleRemoveOption(optionIndex)}
                    >
                      <MdDelete />
                    </Button>
                  )}
                </div>

                <Row>
                  <Col md="6">
                    <FormGroup className="my-0">
                      <Label>
                        Nota <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="number"
                        min={min}
                        max={max}
                        placeholder={`${min} - ${max}`}
                        value={opt.rate}
                        onChange={(e) =>
                          handleOptionChange(
                            optionIndex,
                            'rate',
                            e.target.value
                          )
                        }
                        required
                      />
                    </FormGroup>
                  </Col>

                  <Col md="6">
                    {isTagType ? (
                      <FormGroup className="my-0">
                        <Label>
                          Tag <span className="text-danger">*</span>
                        </Label>
                        <Select
                          options={tagOptions}
                          value={
                            tagOptions?.find((t) => t.value === opt.tag) || null
                          }
                          onChange={(val) => {
                            handleOptionChange(optionIndex, 'tag', val?.value);
                          }}
                          placeholder="Selecione ..."
                          noOptionsMessage={() => 'Nenhuma tag encontrada'}
                          classNamePrefix="react-select"
                          isClearable
                          required
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              minHeight: '46px'
                            })
                          }}
                        />
                      </FormGroup>
                    ) : (
                      <FormGroup className="my-0">
                        <Label>Rótulo da nota</Label>
                        <Input
                          type="text"
                          value={opt.label || ''}
                          onChange={(e) =>
                            handleOptionChange(
                              optionIndex,
                              'label',
                              e.target.value
                            )
                          }
                        />
                      </FormGroup>
                    )}
                  </Col>
                </Row>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
}

export function ConfigSurveyQuestion({
  questionData,
  index,
  allQuestions,
  handleUpdateQuestion,
  handleRemoveQuestion,
  handleQuestionsChange,
  questionAudioOptions,
  tagOptions
}) {
  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const typeValue = questionData?.type?.value || questionData?.type;

  const availableTypeOptions = useMemo(() => {
    let allowedOptions = [...typesOfResponseOptions];

    // Regra para pergunta principal: deve ser NPS ou CSAT
    if (questionData?.isMain) {
      allowedOptions = allowedOptions.filter((opt) =>
        ['NPS', 'CSAT'].includes(opt.value)
      );
    }

    const otherQuestions = (allQuestions || []).filter((_, i) => i !== index);

    // Verifica se já existe alguma outra pergunta que seja NPS ou CSAT
    const hasExistingRating = otherQuestions.some((q) => {
      const qType = q.type?.value || q.type;
      return ['NPS', 'CSAT'].includes(qType);
    });

    // Se já existe uma NPS ou CSAT na pesquisa (fora a atual),
    // as únicas opções permitidas para esta pergunta são TAG e Texto Livre.
    if (hasExistingRating) {
      allowedOptions = allowedOptions.filter((opt) =>
        ['TAG', 'FREE_TEXT'].includes(opt.value)
      );
    }

    return allowedOptions;
  }, [questionData?.isMain, allQuestions, index]);

  const handleSetIsMain = (e) => {
    e.stopPropagation();

    const updatedQuestions = allQuestions.map((q, i) => ({
      ...q,
      isMain: i === index
    }));

    if (handleQuestionsChange) {
      handleQuestionsChange(updatedQuestions);
    } else {
      handleUpdateQuestion(index, 'isMain', true);
    }
  };

  const handleRemove = useCallback(
    (e) => {
      e.stopPropagation();
      handleRemoveQuestion(index);
    },
    [index, handleRemoveQuestion]
  );

  const handleChange = (field, value) => {
    handleUpdateQuestion(index, field, value);
  };

  const handleTypeChange = (selectedOption) => {
    const newTypeValue = selectedOption?.value || selectedOption;
    const newOptions = generateDefaultOptions(newTypeValue);
    const newAudioId =
      newTypeValue === 'FREE_TEXT' ? null : questionData?.audioId;

    if (handleQuestionsChange && allQuestions) {
      const updatedQuestions = [...allQuestions];
      updatedQuestions[index] = {
        ...updatedQuestions[index],
        type: selectedOption,
        options: newOptions,
        audioId: newAudioId
      };
      handleQuestionsChange(updatedQuestions);
    } else {
      handleChange('type', selectedOption);
      if (newTypeValue === 'FREE_TEXT') {
        handleChange('audioId', null);
      }
      setTimeout(() => {
        handleChange('options', newOptions);
      }, 100);
    }
  };

  const handleOptionsChange = (newOptions) => {
    handleChange('options', newOptions);
  };

  return (
    <ConfigSurvey.QuestionContainer>
      <ConfigSurvey.Accordion>
        <ConfigSurvey.AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <div className="d-flex justify-content-between w-100 align-items-center pr-3">
            <Col md="auto px-0">
              <span>{questionData?.name || 'Nova Pergunta'}</span>
              {questionData?.type?.value === 'FREE_TEXT' && (
                <FormTooltip text="Perguntas do tipo Texto Livre não estarão presentes nas pesquisas de telefonia." />
              )}
            </Col>
            <Col md="auto">
              {questionData?.isMain && (
                <Button
                  size="sm"
                  type="button"
                  style={{
                    backgroundColor: systemConfiguration.primary_color,
                    color: '#fff',
                    outline: 'none',
                    border: 'none',
                    cursor: 'default'
                  }}
                >
                  Pergunta Principal
                </Button>
              )}
              {!questionData?.isMain &&
                ['CSAT', 'NPS'].includes(questionData?.type?.value) && (
                  <Button
                    color="light"
                    size="sm"
                    type="button"
                    onClick={handleSetIsMain}
                  >
                    Tornar Principal
                  </Button>
                )}
              <Button
                color="danger"
                size="sm"
                type="button"
                className="ml-2"
                title="Remover Pergunta"
                onClick={handleRemove}
              >
                <MdDelete />
              </Button>
            </Col>
          </div>
        </ConfigSurvey.AccordionSummary>

        <ConfigSurvey.AccordionDetails>
          <Row className="mt-3">
            <Col md="6">
              <FormGroup className="my-0">
                <Label>
                  Nome da Pergunta (Controle Interno)
                  <span className="text-danger">*</span>
                  <FormTooltip text="O nome da pergunta é utilizado como cabeçalho nas exportações dos relatórios." />
                </Label>
                <Input
                  type="text"
                  value={questionData?.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                />
              </FormGroup>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md="6">
              <FormGroup className="my-0">
                <Label>
                  Tipo da resposta <span className="text-danger">*</span>
                </Label>
                <Select
                  value={
                    typesOfResponseOptions.find(
                      (opt) =>
                        opt.value ===
                        (questionData?.type?.value || questionData?.type)
                    ) || questionData?.type
                  }
                  onChange={handleTypeChange}
                  options={availableTypeOptions}
                  noOptionsMessage={() =>
                    'Opção indisponível para esta configuração'
                  }
                  classNamePrefix="react-select"
                />
              </FormGroup>
            </Col>
            {typeValue !== 'FREE_TEXT' && (
              <Col md="6">
                <FormGroup className="my-0">
                  <Label>Áudio da questão</Label>
                  <Select
                    value={questionData?.audioId}
                    onChange={(val) => handleChange('audioId', val)}
                    options={questionAudioOptions}
                    placeholder="Selecione..."
                    classNamePrefix="react-select"
                    isClearable
                  />
                </FormGroup>
              </Col>
            )}
          </Row>

          <Row className="mt-3">
            <Col md="12">
              <FormGroup className="my-0">
                <Label>
                  Mensagem da questão (para o usuário)
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  value={questionData?.message || ''}
                  onChange={(e) => handleChange('message', e.target.value)}
                  required
                />
              </FormGroup>
            </Col>
          </Row>

          <QuestionOptions
            options={questionData?.options}
            questionType={questionData?.type}
            onChange={handleOptionsChange}
            tagOptions={tagOptions}
          />

          <Row hidden>
            <Col md="6">
              <Input
                type="text"
                value={questionData?.id || ''}
                readOnly
                disabled
              />
            </Col>
          </Row>
        </ConfigSurvey.AccordionDetails>
      </ConfigSurvey.Accordion>
    </ConfigSurvey.QuestionContainer>
  );
}
